import { Middleware } from 'redux'
import * as Sentry from '@sentry/browser'

import { isSentryWatchedUser } from 'lib/helpers'
import {
  logout,
  addActionToRetryQueue,
  selectRefreshAccessToken,
  refreshAccessToken,
  refreshAccessTokenActionType,
  selectRefreshInProgress,
  selectLastRefreshTime,
  setRefreshInProgress,
  updateLastRefreshTime,
  generateRefreshId,
  clearRefreshInProgress,
  selectRetryCount,
  scheduleTokenRetry,
  incrementRetryCount,
  selectScheduledRetries,
  clearScheduledRetry,
} from 'store/ducks/auth'
import { addError } from 'store/slices/recaptcha'
import { logoutActionType } from '../ducks/auth'
import { userApi } from 'store/api/yay/user'

const MAX_REFRESH_RETRIES = 3
const RETRY_DELAY_MS = 2000 // 2 sec
const RATE_LIMIT_WINDOW = 60000 // 1 min
const MAX_REQUESTS_PER_WINDOW = 5 // max 5 refresh requests per minute
const REFRESH_COOLDOWN_MS = 5000 // min 5 sec between refresh attempts
const refreshRequestTimestamps = []

const canMakeRefreshRequest = (state) => {
  const now = Date.now()

  const refreshInProgress = selectRefreshInProgress(state)
  const lastRefreshTime = selectLastRefreshTime(state)
  const timeSinceLastRefresh = now - lastRefreshTime

  if (refreshInProgress) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Refresh already in progress',
      level: 'warning',
      data: { refreshInProgress },
    })
    return false
  }

  if (timeSinceLastRefresh < REFRESH_COOLDOWN_MS) {
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'In refresh cooldown period',
      level: 'warning',
      data: {
        timeSinceLastRefresh,
        cooldownPeriod: REFRESH_COOLDOWN_MS,
      },
    })
    return false
  }

  // go through refresh request timestamps
  // remove timestamps older than window
  while (
    refreshRequestTimestamps.length > 0 &&
    refreshRequestTimestamps[0] < now - RATE_LIMIT_WINDOW
  ) {
    refreshRequestTimestamps.shift()
  }

  // check if under the limit
  if (refreshRequestTimestamps.length < MAX_REQUESTS_PER_WINDOW) {
    refreshRequestTimestamps.push(now)
    return true
  }

  Sentry.addBreadcrumb({
    category: 'auth',
    message: 'Rate limit reached for refresh token requests',
    level: 'warning',
    data: {
      requestsInWindow: refreshRequestTimestamps.length,
      windowSizeMs: RATE_LIMIT_WINDOW,
      maxRequestsAllowed: MAX_REQUESTS_PER_WINDOW,
    },
  })

  return false
}

const errorHandlerMiddleware: Middleware = (store) => (next) => (action) => {
  const state = store.getState()
  const currentUserId =
    userApi.endpoints.getCurrentUser.select()(state)?.data?.id
  const isWatchedUser = isSentryWatchedUser(currentUserId)

  // enhanced logging for auth errors
  if (action.type?.includes('failure') && action.error) {
    const errorData = {
      actionType: action.type,
      errorCode: action?.error?.response?.data?.error_code,
      status: action?.error?.response?.status,
      statusText: action?.error?.response?.statusText,
      url: action?.error?.response?.config?.url,
      requestMethod: action?.error?.response?.config?.method,
      requestHeaders: action?.error?.config?.headers,
      responseHeaders: action?.error?.response?.headers,
      refreshId: action.meta?.refreshId,
      retryCount: action.meta?.retryCount,
      onlineStatus: navigator.onLine, // check if user is online when retrying
      timestamp: new Date().toISOString(),
    }

    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'Auth error',
      level: 'error',
      data: errorData,
    })

    console.error('Auth Error:', errorData)
  }

  const errorCode =
    action?.error?.response?.data?.error_code ??
    action?.payload?.data?.error_code ??
    ''

  // parse HTTP status for when error code is missing but we have a 401
  const httpStatus = action?.error?.response?.status
  const is401Error = httpStatus === 401

  const refreshId = action.meta?.refreshId || generateRefreshId()

  // if logout failed, just ignore it
  if (![logoutActionType.failure].includes(action.type)) {
    switch (errorCode) {
      case -18: // invalid token
        Sentry.captureMessage('Invalid token error (-18): Logging out user', {
          level: 'warning',
          extra: {
            action: action.type,
            timestamp: new Date().toISOString(),
          },
        })
        return store.dispatch(logout())

      case -3: // access token expire
        if (action.meta?.previousAction) {
          // add token expire action to retry queue
          store.dispatch(addActionToRetryQueue(action.meta?.previousAction))
        }

        // fetch new access token if not rate limited
        if (
          !selectRefreshAccessToken(store.getState()).isLoading &&
          canMakeRefreshRequest(store.getState())
        ) {
          store.dispatch(setRefreshInProgress(refreshId))
          store.dispatch(updateLastRefreshTime())

          if (isWatchedUser) {
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Attempting to refresh access token',
              level: 'info',
              data: {
                timestamp: new Date().toISOString(),
                refreshId,
              },
            })
          }

          store.dispatch(refreshAccessToken({ refreshId }))

          // wait a while then reset refreshInProgress flag
          setTimeout(() => {
            const state = store.getState()
            const stillInProgress = selectRefreshInProgress(state)

            if (isWatchedUser && stillInProgress) {
              Sentry.addBreadcrumb({
                category: 'auth',
                message: 'Clearing stale refresh in progress state',
                level: 'warning',
                data: { refreshId },
              })

              store.dispatch(clearRefreshInProgress(refreshId))
            }
          }, 10000)

          return action
        }

        // could not make request due to rate limiting
        if (
          isWatchedUser &&
          selectRefreshAccessToken(store.getState()).isLoading
        ) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message:
              'Refresh token request already in progress; not dispatching new requests',
            level: 'info',
            data: { refreshId },
          })
        }

        return action

      case -29: // recaptcha required
        if (action.meta?.previousAction) {
          return store.dispatch(addError(action.meta?.previousAction))
        }
        break
    }

    // log 401 errors that didn't have a specific error code
    if (is401Error && !errorCode) {
      if (isWatchedUser) {
        Sentry.captureMessage(
          'Unspecified 401 error: Attempting token refresh',
          {
            level: 'warning',
            extra: {
              action: action.type,
              url: action?.error?.response?.config?.url,
              refreshId,
            },
          }
        )
      }

      if (
        !selectRefreshAccessToken(store.getState()).isLoading &&
        canMakeRefreshRequest(store.getState())
      ) {
        store.dispatch(setRefreshInProgress(refreshId))
        store.dispatch(updateLastRefreshTime())

        if (isWatchedUser) {
          Sentry.addBreadcrumb({
            category: 'auth',
            message:
              'Attempting to refresh access token for 401 without error code',
            level: 'info',
            data: { refreshId },
          })
        }

        store.dispatch(refreshAccessToken())

        // wait a while then reset refreshInProgress flag
        setTimeout(() => {
          const state = store.getState()
          const stillInProgress = selectRefreshInProgress(state)

          if (stillInProgress) {
            store.dispatch(clearRefreshInProgress(refreshId))
          }
        }, 10000)

        return action
      }
    }
  }

  // force logout if refresh fails
  if (action.type === refreshAccessTokenActionType.failure) {
    const isOnline = navigator.onLine

    // clear the in-progress flag for this refresh attempt
    store.dispatch(clearRefreshInProgress(refreshId))

    if (isWatchedUser) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: isOnline
          ? 'Refresh failed: logging out user'
          : 'Refresh failed - user appears offline: logging out',
        level: 'warning',
        data: {
          refreshId,
          isOnline,
          errorDetails: {
            code: errorCode,
            status: httpStatus,
            message: action?.error?.message,
          },
        },
      })
    }

    return store.dispatch(logout())
  }

  // handle refresh token success
  if (action.type === refreshAccessTokenActionType.success) {
    if (action.meta?.refreshId) {
      store.dispatch(clearRefreshInProgress(action.meta.refreshId))
    }

    const wasRetryAttempt = action.meta?.retryCount > 0
    if (isWatchedUser) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: wasRetryAttempt
          ? 'Token refresh retry successful'
          : 'Token refresh successful',
        level: 'info',
        data: {
          refreshId: action.meta?.refreshId,
          retryCount: action.meta?.retryCount + 1,
          wasRetryAttempt,
        },
      })
    }
  }

  return next(action)
}

export default errorHandlerMiddleware
